import { Button } from "@nimit9/signet-ui/components/button"
import { KeySequence } from "@nimit9/signet-ui/components/kbd"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { Link, useRouterState } from "@tanstack/react-router"
import {
  ArrowLeftRight,
  BarChart3,
  Briefcase,
  Calculator,
  CalendarClock,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  Flag,
  Landmark,
  LayoutDashboard,
  Lock,
  LogOut,
  type LucideIcon,
  RefreshCw,
  Settings,
  Shield,
  Target,
  TrendingUp,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { DevUserSwitcher } from "@/components/auth/dev-user-switcher"
import { PaisaMark } from "@/components/common/paisa-mark"
import { AlertBell } from "@/components/layout/alert-bell"
import { useHousehold } from "@/hooks/use-household"
import { useDataLocked } from "@/hooks/use-veiled"
import { cn } from "@/lib/cn"
import { signOut } from "@/lib/sign-out"
import { useUIStore } from "@/store/ui"

/** Go-to shortcuts registered in __root.tsx, shown on hover. */
export const NAV_KEYS: Record<string, string[]> = {
  "/": ["g", "d"],
  "/transactions": ["g", "t"],
  "/analytics": ["g", "a"],
  "/budgets": ["g", "b"],
  "/upcoming": ["g", "u"],
  "/net-worth": ["g", "n"],
  "/investments": ["g", "i"],
  "/goals": ["g", "g"],
  "/calculators": ["g", "c"],
  "/reports": ["g", "r"],
}

const navSections: Array<{
  label: string
  items: Array<{ label: string; to: string; icon: LucideIcon }>
}> = [
  {
    label: "Overview",
    items: [
      { label: "Dashboard", to: "/", icon: LayoutDashboard },
      { label: "Transactions", to: "/transactions", icon: ArrowLeftRight },
      { label: "Analytics", to: "/analytics", icon: BarChart3 },
      { label: "Budgets", to: "/budgets", icon: Target },
      { label: "Upcoming", to: "/upcoming", icon: CalendarClock },
      { label: "Subscriptions", to: "/subscriptions", icon: RefreshCw },
    ],
  },
  {
    label: "Wealth",
    items: [
      { label: "Net Worth", to: "/net-worth", icon: TrendingUp },
      { label: "Investments", to: "/investments", icon: Briefcase },
      { label: "Goals", to: "/goals", icon: Flag },
      { label: "Debt", to: "/debt", icon: CreditCard },
      { label: "Insurance", to: "/insurance", icon: Shield },
    ],
  },
  {
    label: "Tools",
    items: [
      { label: "Tax", to: "/tax", icon: Landmark },
      { label: "Calculators", to: "/calculators", icon: Calculator },
      { label: "Reports", to: "/reports", icon: FileText },
      { label: "Heatmap", to: "/heatmap", icon: CalendarRange },
    ],
  },
]

export function Sidebar() {
  const router = useRouterState()
  const currentPath = router.location.pathname
  const openCommandPalette = useUIStore((s) => s.openCommandPalette)
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed)
  const toggleSidebarCollapsed = useUIStore((s) => s.toggleSidebarCollapsed)
  const { data: householdData } = useHousehold()
  // Member names are user data: while private mode is locked (the veil is up,
  // or the mode isn't known yet) the footer shows a neutral placeholder.
  const dataLocked = useDataLocked()
  const members = dataLocked ? [] : (householdData?.members ?? [])
  const displayName = dataLocked
    ? "Locked"
    : members.map((m) => m.name || m.email.split("@")[0]).join(" + ")
  const initial = (members[0]?.name?.[0] || members[0]?.email?.[0] || "?").toUpperCase()

  // Desktop-only component (the phone tab bar + "More" page cover navigation
  // below the md breakpoint) — still needs isDesktop in JS, not just CSS,
  // because the collapse/hover-expand behavior below must not run on phone.
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const [hovered, setHovered] = useState(false)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const col = sidebarCollapsed && isDesktop
  // Effective collapsed state — false when user is hovering over a collapsed sidebar
  const isCollapsed = col && !hovered

  const startHover = () => {
    if (!col) return
    hoverTimer.current = setTimeout(() => {
      hoverTimer.current = null
      setHovered(true)
    }, 400)
  }
  // Full collapse: cancels pending timer AND clears hover state (used on aside mouseleave)
  const cancelHover = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current)
      hoverTimer.current = null
    }
    setHovered(false)
  }
  // Timer-only cancel: stops expansion from starting, but never collapses an already-expanded sidebar.
  // Used on the toggle button so its CSS-transition sweep doesn't kill an active hover.
  const cancelHoverTimer = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current)
      hoverTimer.current = null
    }
  }

  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
    },
    [],
  )

  return (
    <>
      {/*
        Layout placeholder — desktop only.
        Controls how much space the sidebar occupies in the flex row.
        Only changes width on permanent toggle (col), never on hover,
        so hover-expand floats over content without shifting it.
        Below md, the phone tab bar + "More" page own navigation instead.
      */}
      <div
        className={cn(
          "hidden md:block shrink-0 transition-[width] duration-200",
          col ? "w-[72px]" : "w-[240px]",
        )}
      />

      <aside
        onMouseEnter={startHover}
        onMouseLeave={cancelHover}
        className={cn(
          "fixed inset-y-0 left-0 z-50 hidden h-screen shrink-0 flex-col border-r border-border-default bg-bg-surface transition-[width] duration-200 md:flex",
          // Width follows effective (hover-aware) collapsed state
          isCollapsed ? "md:w-[72px]" : "md:w-[240px]",
          // Subtle shadow when floating over content on hover-expand
          col && hovered && "shadow-2xl",
        )}
      >
        {/* Right-edge collapse anchor */}
        <button
          type="button"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={toggleSidebarCollapsed}
          onMouseEnter={(e) => {
            e.stopPropagation()
            cancelHoverTimer()
          }}
          onMouseLeave={(e) => e.stopPropagation()}
          className="absolute right-0 top-1/2 flex h-10 w-4 -translate-y-1/2 translate-x-full items-center justify-center rounded-r-md border border-l-0 border-border-default bg-bg-surface text-text-hint transition-colors hover:bg-bg-elevated hover:text-text-primary"
        >
          {isCollapsed ? <ChevronRight className="size-3" /> : <ChevronLeft className="size-3" />}
        </button>

        {/* Logo */}
        <div
          className={cn(
            "flex h-[3.25rem] shrink-0 items-center border-b border-border-default",
            isCollapsed ? "justify-center px-0" : "px-4",
          )}
        >
          {isCollapsed ? (
            <PaisaMark size={22} aria-label="Paisa" />
          ) : (
            <PaisaMark variant="horizontal" size={16} aria-label="Paisa" />
          )}
        </div>

        {/* Search trigger */}
        {!isCollapsed && (
          <div className="px-3 py-2">
            <button
              type="button"
              onClick={openCommandPalette}
              className="flex w-full items-center gap-2 rounded-lg border border-border-default bg-bg-elevated px-3 py-1.5 text-left text-caption text-text-hint transition-colors hover:border-border-strong hover:text-text-muted"
            >
              <svg
                aria-hidden="true"
                className="h-3 w-3 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                />
              </svg>
              <span className="flex-1">Search…</span>
              <kbd className="rounded bg-bg-surface px-1 py-0.5 text-label">⌘K</kbd>
            </button>
          </div>
        )}

        {isCollapsed && (
          <div className="flex justify-center py-2">
            <button
              type="button"
              title="Search (⌘K)"
              onClick={openCommandPalette}
              className="flex items-center justify-center rounded-md p-2 text-text-muted transition-colors hover:bg-bg-elevated hover:text-text-primary"
            >
              <svg
                aria-hidden="true"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Nav */}
        <nav
          aria-label="Main navigation"
          className={cn(
            "flex-1 overflow-y-auto py-2 flex flex-col",
            isCollapsed ? "px-1.5 gap-1" : "px-2 gap-4",
          )}
        >
          {navSections.map((section) => (
            <div key={section.label}>
              {!isCollapsed && (
                <p className="mb-1 px-3 text-label font-semibold tracking-wide text-text-hint">
                  {section.label}
                </p>
              )}
              {isCollapsed && <div className="my-1 h-px bg-border-subtle" />}
              {section.items.map((item) => {
                const isActive =
                  item.to === "/" ? currentPath === "/" : currentPath.startsWith(item.to)
                const Icon = item.icon
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    title={isCollapsed ? item.label : undefined}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      "group flex items-center rounded-md transition-colors",
                      isCollapsed ? "justify-center p-2" : "gap-2.5 px-3 py-2 text-body",
                      isActive
                        ? "bg-accent-green/10 text-accent-green font-medium"
                        : "text-text-secondary hover:bg-bg-elevated hover:text-text-primary",
                    )}
                  >
                    <Icon
                      className={cn(
                        "shrink-0",
                        isCollapsed ? "size-[1.125rem]" : "size-4",
                        isActive ? "text-accent-green" : "text-text-muted",
                      )}
                    />
                    {!isCollapsed && item.label}
                    {!isCollapsed && NAV_KEYS[item.to] && (
                      // Desktop-only, low contrast until the row is hovered (#10).
                      <KeySequence
                        keys={NAV_KEYS[item.to]}
                        className="ml-auto hidden opacity-0 transition-opacity group-hover:opacity-100 md:inline-flex"
                      />
                    )}
                  </Link>
                )
              })}
            </div>
          ))}
        </nav>

        {/* Bottom */}
        <div className={cn("border-t border-border-default py-3", isCollapsed ? "px-1.5" : "px-2")}>
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-1">
              <Link
                to="/settings"
                title="Settings"
                className="flex items-center justify-center rounded-md p-2 text-text-muted transition-colors hover:bg-bg-elevated hover:text-text-primary"
              >
                <Settings className="size-[1.125rem]" />
              </Link>
              <div className="flex items-center justify-center rounded-md">
                <AlertBell />
              </div>
              <Button
                variant="ghost"
                size="icon"
                title="Sign out"
                className="h-8 w-8"
                onClick={() => signOut()}
              >
                <LogOut className="h-[1.125rem] w-[1.125rem]" />
              </Button>
            </div>
          ) : (
            <>
              {/* Settings + alerts, then the signed-in profile: one aligned column,
                  same left inset (px-3) and row height as the nav links above. */}
              <div className="flex items-center justify-between gap-1">
                <Link
                  to="/settings"
                  className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-3 py-2 text-body text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary"
                >
                  <Settings className="size-4 shrink-0 text-text-muted" />
                  Settings
                </Link>
                <div className="shrink-0 pr-1">
                  <AlertBell />
                </div>
              </div>
              <DevUserSwitcher />
              <div className="mt-1 flex items-center justify-between gap-1">
                <Link
                  to="/settings/profile"
                  className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-3 py-2 transition-colors hover:bg-bg-elevated"
                  title={dataLocked ? "Locked" : displayName}
                >
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent-green/15 text-label font-semibold text-accent-green">
                    {dataLocked ? <Lock className="size-3" aria-hidden /> : initial}
                  </span>
                  <span className="min-w-0 truncate text-body text-text-secondary">
                    {displayName}
                  </span>
                </Link>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  title="Sign out"
                  aria-label="Sign out"
                  className="mr-1 shrink-0 text-text-muted hover:text-text-primary"
                  onClick={() => signOut()}
                >
                  <LogOut className="size-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  )
}
